import React, { Fragment, useContext, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { Badge, Card } from "react-bootstrap";
import ApiService from "../../../../services/ApiService";
import PageHeaderBar from "../../../layouts/PageHeaderBar";
import { epochToDateTimeString } from "../../../../utils";
import { useDocumentTitle } from "../../../utils";
import { ThemeContext } from "../../../../context/ThemeContext";
import { useAuth } from "../../../Providers/LandmarkAuthContext";
import EmployeeInfo from "./EmployeeInfo";
import landmarkImg from "../../../../images/landmark/logo-text-dark.png";

const DEFAULT_JOB_CONFIG = {
  scheduleInterval: "daily",
  searchQueries: "",
  authorUrls: "",
  postedLimit: "24h",
  maxPosts: 1,
  maxComments: 0,
  maxReactions: 0,
  profileScraperMode: "short",
  scrapeComments: true,
  scrapeReactions: true,
};

const slugifyProgramId = (value = "") =>
  value
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-_]/g, "")
    .replace(/-+/g, "-");

const parseList = (value = "") =>
  value
    .split(/[\n,]/g)
    .map((item) => item.trim())
    .filter(Boolean);

const isValidEmail = (value = "") =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((value || "").trim());

const LeadGeneratorForm = ({ employee }) => {
  const [programName, setProgramName] = useState("");
  const [programId, setProgramId] = useState("");
  const [programIdEdited, setProgramIdEdited] = useState(false);
  const [createRecipientEmail, setCreateRecipientEmail] = useState("");
  const [createRecipients, setCreateRecipients] = useState([]);
  const [editProgramId, setEditProgramId] = useState("");
  const [editProgramName, setEditProgramName] = useState("");
  const [editProgramSlug, setEditProgramSlug] = useState("");
  const [editProgramSlugEdited, setEditProgramSlugEdited] = useState(false);
  const [programs, setPrograms] = useState([]);
  const [loadingPrograms, setLoadingPrograms] = useState(false);
  const [selectedProgramId, setSelectedProgramId] = useState("");
  const [jobs, setJobs] = useState([]);
  const [runs, setRuns] = useState([]);
  const [leadsCount, setLeadsCount] = useState(0);
  const [jobConfig, setJobConfig] = useState(DEFAULT_JOB_CONFIG);
  const [creatingJob, setCreatingJob] = useState(false);
  const [recipientEmail, setRecipientEmail] = useState("");
  const [programRecipients, setProgramRecipients] = useState([]);

  const selectedProgram = useMemo(
    () => programs.find((p) => p.programId === selectedProgramId),
    [programs, selectedProgramId],
  );

  const fetchPrograms = async () => {
    try {
      setLoadingPrograms(true);
      const api = new ApiService({ employee });
      const data = await api.get("landmark/linkedinLeadGenerator/programs");
      setPrograms(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load programs");
    } finally {
      setLoadingPrograms(false);
    }
  };

  const fetchProgramData = async (programId) => {
    if (!programId) return;
    try {
      const api = new ApiService({ employee });
      const [jobsData, runsData, leadsData] = await Promise.all([
        api.get(`landmark/linkedinLeadGenerator/programs/${programId}/jobs`),
        api.get(`landmark/linkedinLeadGenerator/programs/${programId}/runs`),
        api.get(`landmark/linkedinLeadGenerator/programs/${programId}/leads`),
      ]);
      setJobs(Array.isArray(jobsData) ? jobsData : []);
      setRuns(Array.isArray(runsData) ? runsData : []);
      setLeadsCount(Array.isArray(leadsData) ? leadsData.length : 0);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load program data");
    }
  };

  useEffect(() => {
    fetchPrograms();
  }, []);

  useEffect(() => {
    if (!programIdEdited) {
      setProgramId(programName ? slugifyProgramId(programName) : "");
    }
  }, [programName, programIdEdited]);

  useEffect(() => {
    if (employee?.email) {
      setCreateRecipients((prev) => {
        const next = Array.isArray(prev) ? prev : [];
        if (next.includes(employee.email)) return next;
        return [...next, employee.email];
      });
    }
  }, [employee?.email]);

  useEffect(() => {
    fetchProgramData(selectedProgramId);
  }, [selectedProgramId]);

  useEffect(() => {
    if (!selectedProgram) return;
    setProgramRecipients(selectedProgram.notifyEmails || []);
  }, [selectedProgram]);

  useEffect(() => {
    if (!editProgramSlugEdited) {
      setEditProgramSlug(editProgramName ? slugifyProgramId(editProgramName) : "");
    }
  }, [editProgramName, editProgramSlugEdited]);

  const handleCreateProgram = async () => {
    if (!programName.trim()) {
      toast.error("Program name is required");
      return;
    }
    if (!programId.trim()) {
      toast.error("Program ID is required");
      return;
    }
    try {
      const api = new ApiService({ employee });
      const payload = {
        name: programName.trim(),
        programId: slugifyProgramId(programId),
        notifyEmails: createRecipients,
        employee,
      };
      const { success, error, programId: createdProgramId } = await api.post(
        "landmark/linkedinLeadGenerator/programs",
        payload,
      );
      if (!success) {
        toast.error(error || "Failed to create program");
        return;
      }
      toast.success("Program created");
      setProgramName("");
      setProgramId("");
      setProgramIdEdited(false);
      setCreateRecipients([]);
      setCreateRecipientEmail("");
      await fetchPrograms();
      if (createdProgramId) setSelectedProgramId(createdProgramId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to create program");
    }
  };

  const handleUpdateProgram = async (programId, updates = {}) => {
    try {
      const api = new ApiService({ employee });
      const { success, error } = await api.put(
        `landmark/linkedinLeadGenerator/programs/${programId}`,
        updates,
      );
      if (!success) {
        toast.error(error || "Failed to update program");
        return;
      }
      toast.success("Program updated");
      await fetchPrograms();
    } catch (err) {
      console.error(err);
      toast.error("Failed to update program");
    }
  };

  const handleStartEditProgram = (program) => {
    setSelectedProgramId(program.programId);
    setEditProgramId(program.programId);
    setEditProgramName(program.name || "");
    setEditProgramSlug(program.programId || "");
    setEditProgramSlugEdited(false);
  };

  const handleSaveEditProgram = async () => {
    if (!editProgramId) return;
    if (!editProgramName.trim()) {
      toast.error("Program name is required");
      return;
    }
    if (!editProgramSlug.trim()) {
      toast.error("Program ID is required");
      return;
    }
    const updates = {
      name: editProgramName.trim(),
      programId: slugifyProgramId(editProgramSlug),
      notifyEmails: programRecipients,
    };
    await handleUpdateProgram(editProgramId, updates);
    setEditProgramId("");
    setEditProgramName("");
    setEditProgramSlug("");
    setEditProgramSlugEdited(false);
  };

  const handleAddCreateRecipient = () => {
    if (!isValidEmail(createRecipientEmail)) {
      toast.error("Please enter a valid email");
      return;
    }
    const nextRecipients = Array.from(
      new Set([...(createRecipients || []), createRecipientEmail.trim()]),
    );
    setCreateRecipients(nextRecipients);
    setCreateRecipientEmail("");
  };

  const handleRemoveCreateRecipient = (email) => {
    setCreateRecipients((prev) => (prev || []).filter((entry) => entry !== email));
  };

  const handleAddRecipient = async () => {
    if (!selectedProgram) {
      toast.error("Select a program first");
      return;
    }
    if (!isValidEmail(recipientEmail)) {
      toast.error("Please enter a valid email");
      return;
    }

    const nextRecipients = Array.from(
      new Set([...(programRecipients || []), recipientEmail.trim()]),
    );
    await handleUpdateProgram(selectedProgram.programId, {
      notifyEmails: nextRecipients,
    });
    setProgramRecipients(nextRecipients);
    setRecipientEmail("");
  };

  const handleRemoveRecipient = async (email) => {
    if (!selectedProgram) return;
    const nextRecipients = (programRecipients || []).filter(
      (entry) => entry !== email,
    );
    await handleUpdateProgram(selectedProgram.programId, {
      notifyEmails: nextRecipients,
    });
    setProgramRecipients(nextRecipients);
  };

  const handleCreateJob = async () => {
    if (!selectedProgramId) {
      toast.error("Select a program first");
      return;
    }
    try {
      setCreatingJob(true);
      const api = new ApiService({ employee });
      const payload = {
        programId: selectedProgramId,
        scheduleInterval: jobConfig.scheduleInterval,
        searchQueries: parseList(jobConfig.searchQueries),
        authorUrls: parseList(jobConfig.authorUrls),
        postedLimit: jobConfig.postedLimit,
        maxPosts: Number(jobConfig.maxPosts) || 0,
        maxComments: Number(jobConfig.maxComments) || 0,
        maxReactions: Number(jobConfig.maxReactions) || 0,
        profileScraperMode: jobConfig.profileScraperMode,
        scrapeComments: !!jobConfig.scrapeComments,
        scrapeReactions: !!jobConfig.scrapeReactions,
      };
      const { success, error } = await api.post(
        "landmark/linkedinLeadGenerator/jobs",
        payload,
      );
      if (!success) {
        toast.error(error || "Failed to create job");
        return;
      }
      toast.success("Job created");
      setJobConfig(DEFAULT_JOB_CONFIG);
      fetchProgramData(selectedProgramId);
      fetchPrograms();
    } catch (err) {
      console.error(err);
      toast.error("Failed to create job");
    } finally {
      setCreatingJob(false);
    }
  };

  const handleRunJob = async (jobId) => {
    try {
      const api = new ApiService({ employee });
      const { success, error } = await api.post(
        `landmark/linkedinLeadGenerator/jobs/${jobId}/run`,
        {},
      );
      if (!success) {
        toast.error(error || "Failed to run job");
        return;
      }
      toast.success("Job queued");
      fetchProgramData(selectedProgramId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to run job");
    }
  };

  const handleRunDueJobs = async () => {
    try {
      const api = new ApiService({ employee });
      const { success, error } = await api.post(
        "landmark/linkedinLeadGenerator/runDueJobs",
        {},
      );
      if (!success) {
        toast.error(error || "Failed to run due jobs");
        return;
      }
      toast.success("Due jobs queued");
      fetchProgramData(selectedProgramId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to run due jobs");
    }
  };

  const handleToggleJobActive = async (job) => {
    try {
      const api = new ApiService({ employee });
      const { success, error } = await api.put(
        `landmark/linkedinLeadGenerator/jobs/${job.jobId}`,
        { active: !job.active },
      );
      if (!success) {
        toast.error(error || "Failed to update job");
        return;
      }
      toast.success("Job updated");
      fetchProgramData(selectedProgramId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update job");
    }
  };

  const handleScheduleChange = async (jobId, scheduleInterval) => {
    try {
      const api = new ApiService({ employee });
      const { success, error } = await api.put(
        `landmark/linkedinLeadGenerator/jobs/${jobId}`,
        { scheduleInterval },
      );
      if (!success) {
        toast.error(error || "Failed to update schedule");
        return;
      }
      toast.success("Schedule updated");
      fetchProgramData(selectedProgramId);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update schedule");
    }
  };

  return (
    <>
      <Card className="p-4 mb-4">
        <h4 className="mb-3">Create Program</h4>
        <div className="lead-gen-form">
          <div className="mb-3">
            <label className="form-label">Program Name</label>
            <input
              className="form-control"
              value={programName}
              onChange={(e) => setProgramName(e.target.value)}
              placeholder="Lead sourcing program name"
            />
          </div>
          <div className="mb-3">
            <label className="form-label">Program ID</label>
            <input
              className="form-control"
              value={programId}
              onChange={(e) => {
                setProgramIdEdited(true);
                setProgramId(slugifyProgramId(e.target.value));
              }}
              placeholder="auto-generated from name"
            />
            <div className="form-text">
              Lowercase, no spaces. You can adjust this.
            </div>
          </div>
          <div className="mb-3">
            <label className="form-label">Notification Recipients</label>
            <div className="d-flex gap-2">
              <input
                className="form-control"
                value={createRecipientEmail}
                onChange={(e) => setCreateRecipientEmail(e.target.value)}
                placeholder="email address"
              />
              <button
                className="btn lead-gen-btn lead-gen-btn-outline"
                onClick={handleAddCreateRecipient}
              >
                Add
              </button>
            </div>
            <div className="mt-2">
              {(createRecipients || []).length ? (
                <div className="d-flex flex-wrap gap-2">
                  {createRecipients.map((email) => (
                    <span
                      key={email}
                      className="badge lead-gen-badge lead-gen-badge-light d-inline-flex align-items-center"
                      style={{ gap: "8px", padding: "8px 10px" }}
                    >
                      {email}
                      <button
                        className="btn btn-sm p-0 lead-gen-remove-btn"
                        onClick={() => handleRemoveCreateRecipient(email)}
                        aria-label={`Remove ${email}`}
                      >
                        <i className="fa-sharp fa-solid fa-delete-left" />
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-muted">No recipients yet.</div>
              )}
            </div>
          </div>
          <button
            className="btn lead-gen-btn lead-gen-btn-primary"
            onClick={handleCreateProgram}
          >
            Create
          </button>
        </div>
      </Card>

      <Card className="p-4 mb-4">
        <div className="d-flex align-items-center justify-content-between">
          <h4 className="mb-0">Programs</h4>
          <button
            className="btn lead-gen-btn lead-gen-btn-outline btn-sm"
            onClick={fetchPrograms}
          >
            Refresh
          </button>
        </div>
        <div className="text-muted small mb-3">
          Select a program to view its jobs, runs, and results.
        </div>
        {loadingPrograms ? (
          <div className="text-muted">Loading programs...</div>
        ) : (
          <div className="table-responsive">
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Jobs</th>
                  <th>Author</th>
                  <th>Created</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {programs.map((program) => (
                  <tr
                    key={program.programId}
                    className={
                      program.programId === selectedProgramId
                        ? "lead-gen-program-row is-selected"
                        : "lead-gen-program-row"
                    }
                    onClick={() => setSelectedProgramId(program.programId)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        setSelectedProgramId(program.programId);
                      }
                    }}
                  >
                    <td>
                      <div className="fw-semibold">{program.name}</div>
                      <div className="text-muted small">{program.programId}</div>
                    </td>
                    <td>
                      <Badge className="lead-gen-badge" bg={program.active ? "success" : "secondary"}>
                        {program.active ? "Active" : "Inactive"}
                      </Badge>
                    </td>
                    <td>{program.sourceJobIds?.length || 0}</td>
                    <td>
                      <div className="fw-semibold">
                        {program?.createdByEmployee?.employeeName || "—"}
                      </div>
                      <div className="text-muted small">
                        {program?.createdByEmployee?.employeeEmail || "—"}
                      </div>
                    </td>
                    <td>
                      {program.createdTime
                        ? epochToDateTimeString(program.createdTime)
                        : "-"}
                    </td>
                    <td className="text-end">
                      <button
                        className="btn btn-sm lead-gen-btn lead-gen-btn-outline me-2"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleStartEditProgram(program);
                        }}
                      >
                        Edit
                      </button>
                      <button
                        className="btn btn-sm lead-gen-btn lead-gen-btn-outline"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleUpdateProgram(program.programId, {
                            active: !program.active,
                          });
                        }}
                      >
                        {program.active ? "Deactivate" : "Activate"}
                      </button>
                    </td>
                  </tr>
                ))}
                {!programs.length && (
                  <tr>
                    <td colSpan={5} className="text-muted text-center py-4">
                      No programs yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {editProgramId && (
        <Card className="p-4 mb-4">
          <h4 className="mb-3">Edit Program: {editProgramName || editProgramId}</h4>
          <div className="lead-gen-form">
            <div className="mb-3">
              <label className="form-label">Program Name</label>
              <input
                className="form-control"
                value={editProgramName}
                onChange={(e) => setEditProgramName(e.target.value)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">Program ID</label>
              <input
                className="form-control"
                value={editProgramSlug}
                onChange={(e) => {
                  setEditProgramSlugEdited(true);
                  setEditProgramSlug(slugifyProgramId(e.target.value));
                }}
              />
              <div className="form-text">
                Lowercase, no spaces. Must be unique.
              </div>
            </div>
            <div className="mb-3">
              <label className="form-label">Notification Recipients</label>
              <div className="d-flex gap-2">
                <input
                  className="form-control"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="email address"
                />
                <button
                  className="btn lead-gen-btn lead-gen-btn-outline"
                  onClick={handleAddRecipient}
                >
                  Add
                </button>
              </div>
              <div className="mt-2">
                {(programRecipients || []).length ? (
                  <div className="d-flex flex-wrap gap-2">
                    {programRecipients.map((email) => (
                      <span
                        key={email}
                        className="badge lead-gen-badge lead-gen-badge-light d-inline-flex align-items-center"
                        style={{ gap: "8px", padding: "8px 10px" }}
                      >
                        {email}
                        <button
                          className="btn btn-sm p-0 lead-gen-remove-btn"
                          onClick={() => handleRemoveRecipient(email)}
                          aria-label={`Remove ${email}`}
                        >
                          <i className="fa-sharp fa-solid fa-delete-left" />
                        </button>
                      </span>
                    ))}
                  </div>
                ) : (
                  <div className="text-muted">No recipients yet.</div>
                )}
              </div>
            </div>
            <div className="d-flex gap-2">
              <button
                className="btn lead-gen-btn lead-gen-btn-primary"
                onClick={handleSaveEditProgram}
              >
                Save Changes
              </button>
              <button
                className="btn lead-gen-btn lead-gen-btn-outline"
                onClick={() => {
                  setEditProgramId("");
                  setEditProgramName("");
                  setEditProgramSlug("");
                  setEditProgramSlugEdited(false);
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </Card>
      )}

      {selectedProgram && (
        <>
          <Card className="p-4 mb-4">
            <div className="d-flex align-items-center justify-content-between">
              <div>
                <div className="text-uppercase text-muted small">Viewing Program</div>
                <h4 className="mb-1">{selectedProgram.name}</h4>
                <div className="text-muted small">
                  Program ID: {selectedProgram.programId}
                </div>
                <div className="text-muted small">
                  Leads found: {leadsCount}
                </div>
              </div>
              <button
                className="btn lead-gen-btn lead-gen-btn-primary"
                onClick={handleRunDueJobs}
              >
                Run Due Jobs
              </button>
            </div>
          </Card>

          <Card className="p-4 mb-4">
            <h4 className="mb-3">Create Job</h4>
            <div className="row g-3">
              <div className="col-md-4">
                <label className="form-label">Schedule</label>
                <select
                  className="form-select"
                  value={jobConfig.scheduleInterval}
                  onChange={(e) =>
                    setJobConfig((prev) => ({
                      ...prev,
                      scheduleInterval: e.target.value,
                    }))
                  }
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Posted Limit</label>
                <select
                  className="form-select"
                  value={jobConfig.postedLimit}
                  onChange={(e) =>
                    setJobConfig((prev) => ({
                      ...prev,
                      postedLimit: e.target.value,
                    }))
                  }
                >
                  <option value="any">any</option>
                  <option value="1h">1h</option>
                  <option value="24h">24h</option>
                  <option value="week">week</option>
                  <option value="month">month</option>
                  <option value="3months">3months</option>
                  <option value="6months">6months</option>
                  <option value="year">year</option>
                </select>
              </div>
              <div className="col-md-4">
                <label className="form-label">Profile Scraper Mode</label>
                <select
                  className="form-select"
                  value={jobConfig.profileScraperMode}
                  onChange={(e) =>
                    setJobConfig((prev) => ({
                      ...prev,
                      profileScraperMode: e.target.value,
                    }))
                  }
                >
                  <option value="short">Short</option>
                  <option value="main">Main</option>
                </select>
              </div>
              <div className="col-md-6">
                <label className="form-label">Search Queries</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={jobConfig.searchQueries}
                  onChange={(e) =>
                    setJobConfig((prev) => ({
                      ...prev,
                      searchQueries: e.target.value,
                    }))
                  }
                  placeholder="one per line or comma separated"
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Author URLs</label>
                <textarea
                  className="form-control"
                  rows={3}
                  value={jobConfig.authorUrls}
                  onChange={(e) =>
                    setJobConfig((prev) => ({
                      ...prev,
                      authorUrls: e.target.value,
                    }))
                  }
                  placeholder="one per line or comma separated"
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Max Posts</label>
                <input
                  type="number"
                  className="form-control"
                  value={jobConfig.maxPosts}
                  onChange={(e) =>
                    setJobConfig((prev) => ({
                      ...prev,
                      maxPosts: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Max Comments</label>
                <input
                  type="number"
                  className="form-control"
                  value={jobConfig.maxComments}
                  onChange={(e) =>
                    setJobConfig((prev) => ({
                      ...prev,
                      maxComments: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Max Reactions</label>
                <input
                  type="number"
                  className="form-control"
                  value={jobConfig.maxReactions}
                  onChange={(e) =>
                    setJobConfig((prev) => ({
                      ...prev,
                      maxReactions: e.target.value,
                    }))
                  }
                />
              </div>
              <div className="col-md-3">
                <div className="form-check mt-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={jobConfig.scrapeComments}
                    onChange={(e) =>
                      setJobConfig((prev) => ({
                        ...prev,
                        scrapeComments: e.target.checked,
                      }))
                    }
                    id="scrapeComments"
                  />
                  <label className="form-check-label" htmlFor="scrapeComments">
                    Scrape Comments
                  </label>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-check mt-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={jobConfig.scrapeReactions}
                    onChange={(e) =>
                      setJobConfig((prev) => ({
                        ...prev,
                        scrapeReactions: e.target.checked,
                      }))
                    }
                    id="scrapeReactions"
                  />
                  <label className="form-check-label" htmlFor="scrapeReactions">
                    Scrape Reactions
                  </label>
                </div>
              </div>
              <div className="col-md-6 d-flex align-items-end justify-content-end">
                <button
                  className="btn lead-gen-btn lead-gen-btn-primary"
                  onClick={handleCreateJob}
                  disabled={creatingJob}
                >
                  {creatingJob ? "Creating..." : "Create Job"}
                </button>
              </div>
            </div>
          </Card>

          <Card className="p-4 mb-4">
            <h4 className="mb-3">Jobs</h4>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Job</th>
                    <th>Status</th>
                    <th>Schedule</th>
                    <th>Next Run</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.map((job) => (
                    <tr key={job.jobId}>
                      <td>
                        <div className="fw-semibold">{job.jobType || "postEngagement"}</div>
                        <div className="text-muted small">{job.jobId}</div>
                      </td>
                      <td>
                        <Badge className="lead-gen-badge" bg={job.active ? "success" : "secondary"}>
                          {job.active ? "Active" : "Inactive"}
                        </Badge>
                      </td>
                      <td>
                        <select
                          className="form-select form-select-sm"
                          value={job.scheduleInterval || "daily"}
                          onChange={(e) =>
                            handleScheduleChange(job.jobId, e.target.value)
                          }
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                        </select>
                      </td>
                      <td>
                        {job.nextRunTime
                          ? epochToDateTimeString(job.nextRunTime)
                          : "-"}
                      </td>
                      <td>
                        <button
                          className="btn btn-sm lead-gen-btn lead-gen-btn-outline me-2"
                          onClick={() => handleRunJob(job.jobId)}
                        >
                          Run Now
                        </button>
                        <button
                          className="btn btn-sm lead-gen-btn lead-gen-btn-outline"
                          onClick={() => handleToggleJobActive(job)}
                        >
                          {job.active ? "Deactivate" : "Activate"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {!jobs.length && (
                    <tr>
                      <td colSpan={5} className="text-muted text-center py-4">
                        No jobs yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>

          <Card className="p-4 mb-4">
            <h4 className="mb-3">Runs</h4>
            <div className="table-responsive">
              <table className="table table-sm align-middle">
                <thead>
                  <tr>
                    <th>Run</th>
                    <th>Status</th>
                    <th>Posts</th>
                    <th>New Profiles</th>
                    <th>Started</th>
                    <th>Completed</th>
                  </tr>
                </thead>
                <tbody>
                  {runs.map((run) => (
                    <tr key={run.runId}>
                      <td>
                        <div className="fw-semibold">{run.runId}</div>
                        <div className="text-muted small">{run.jobId}</div>
                      </td>
                      <td>
                        <Badge className="lead-gen-badge" bg={run.status === "completed" ? "success" : "warning"}>
                          {run.status || "queued"}
                        </Badge>
                      </td>
                      <td>{run.postCount ?? "-"}</td>
                      <td>{run.newCount ?? "-"}</td>
                      <td>
                        {run.startedTime
                          ? epochToDateTimeString(run.startedTime)
                          : "-"}
                      </td>
                      <td>
                        {run.completedTime
                          ? epochToDateTimeString(run.completedTime)
                          : "-"}
                      </td>
                    </tr>
                  ))}
                  {!runs.length && (
                    <tr>
                      <td colSpan={6} className="text-muted text-center py-4">
                        No runs yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </>
  );
};

const Dashboard = () => {
  const { changeBackground } = useContext(ThemeContext);
  const { employee, isAuthenticated, loading, logout } = useAuth();

  useEffect(() => {
    changeBackground({ value: "light", label: "Light" });
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !employee) return;
    try {
      const api = new ApiService({ employee });
      api.get("landmark/employees/login");
    } catch (error) {
      console.warn("employee login touch failed", error);
    }
  }, [isAuthenticated, employee]);

  useDocumentTitle("LinkedIn Lead Generator - Landmark Ventures");

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border" role="status" aria-label="Loading" />
      </div>
    );
  }

  return (
    <Fragment>
      <div className="global-dash-wrapper seamless-bot">
        <div className="d-flex align-items-center justify-content-center">
          <img src={landmarkImg} className="img-fluid landmark-img" alt="Landmark" />
        </div>

        {isAuthenticated && employee && (
          <div className="landmark-sign-in" style={{ marginBottom: "18px" }}>
            <div className="existing-accounts-container">
              <EmployeeInfo
                landmarkEmployee={employee}
                disableHover={true}
                showLogout={true}
                logout={logout}
              />
            </div>
          </div>
        )}

        <PageHeaderBar
          pages={[
            { title: "Landmark", url: `` },
            { title: "LinkedIn Lead Generator", url: `/landmark/linkedin-leads` },
          ]}
        />

        {isAuthenticated ? (
          <LeadGeneratorForm employee={employee} />
        ) : (
          <div className="text-center text-muted p-5">
            Please sign in to access the tool.
          </div>
        )}
      </div>
    </Fragment>
  );
};

export default Dashboard;
